import React from 'react';
import './EntryOverlay.css';
import { EntrySection } from './EntrySection';

interface HobbiesEntryProps {
  onReturnToMap: () => void;
  entryPosition?: { x: number; y: number };
  onNext?: () => void;
  nextLabel?: string;
}

export const HobbiesEntry: React.FC<HobbiesEntryProps> = ({
  onReturnToMap,
  onNext,
  nextLabel,
}: HobbiesEntryProps) => {
  return (
    <div className="entry-overlay">
      <div className="entry-card accent-hobbies">
        <div className="entry-header">
          <h2 className="entry-title">Hobbies</h2>
        </div>

        <div className="entry-body">
          <div className="entry-description">
            <p>
              I've always been competitive — sports, games, or just trying
              to be better than I was yesterday. That drive didn't stop
              when I started writing code.
            </p>
          </div>

          <EntrySection title="Baseball">
            <div className="entry-description">
              <p>
                Baseball was my first love — Dixie League, travel ball,
                American Legion, varsity. Had{' '}
                <a
                  href="https://sc.edu/about/system_and_campuses/salkehatchie/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Division I
                </a>{' '}
                and{' '}
                <a
                  href="https://www.limestone.edu/news/athletics"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Division II
                </a>{' '}
                offers by the end of high school but chose Computer Science
                at Clemson instead. The discipline, the failure rate, the
                team dynamics — a lot of what made me good at baseball
                shows up in how I approach engineering.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="Bowling">
            <div className="entry-description">
              <p>
                I've bowled since I was 11 and I'm deep into the
                competitive side now — local tournaments, travel leagues,
                weekly leagues. I bowl two-handed like{' '}
                <a
                  href="https://en.wikipedia.org/wiki/Jason_Belmonte"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Jason Belmonte
                </a>
                , which always draws a crowd, and I've taken down pros
                like Kyle Troup in local events. What keeps me hooked is
                the physics — lane play, oil patterns, core shapes,
                coverstocks. There's a surprising amount of science behind
                throwing a ball at pins.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="Competitive FPS">
            <div className="entry-description">
              <p>
                FPS games have been my thing since I was a kid. Halo 3 was
                the main title — hit 50 (max rank) in every ranked
                playlist. In Reach, a friend and I competed in tournaments
                and qualifiers against pros like Roy, Heinz, iGotUrPistola,
                and Ogre2 — made it pretty deep in the{' '}
                <a
                  href="https://www.youtube.com/watch?v=zgDnMJQ7FRo"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  BIC® Flex4™ Pro FFA Exhibition
                </a>
                . Beyond Halo, I've put serious time into Counter-Strike
                and Valorant — 10-mans, ESEA, always chasing that next
                competitive edge.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="Rocket League">
            <div className="entry-description">
              <p>
                My newest competitive obsession — recently hit Grand
                Champion, top 0.05% globally. The skill ceiling feels
                infinite, every match is intense, and there's always
                something new to learn. It scratches the same itch that
                drew me to engineering — deep systems you can study forever
                and still find room to improve.
              </p>
            </div>
          </EntrySection>
        </div>

        <div className="entry-footer">
          <button className="entry-button" onClick={onReturnToMap}>
            &larr; Return to Map
          </button>
          {onNext && (
            <button className="entry-button" onClick={onNext}>
              Next: {nextLabel} &rarr;
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
